<template>
  <Teleport to="body">
    <Transition name="toast">
      <div v-if="open" class="toast" role="status" aria-live="polite">
        {{ message }}
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { onBeforeUnmount, watch } from 'vue';

const props = withDefaults(defineProps<{
  open: boolean;
  message?: string;
  duration?: number;
}>(), { duration: 2500 });

const emit = defineEmits<{ 'update:open': [value: boolean] }>();

let timer: number | null = null;

function clearTimer() {
  if (timer != null) {
    window.clearTimeout(timer);
    timer = null;
  }
}

watch(() => props.open, (open) => {
  clearTimer();
  if (open) {
    timer = window.setTimeout(() => {
      timer = null;
      emit('update:open', false);
    }, props.duration);
  }
});

onBeforeUnmount(clearTimer);
</script>

<style scoped lang="scss">
.toast {
  position: fixed;
  top: max(70px, var(--app-safe-top));
  left: 50%;
  transform: translateX(-50%);
  z-index: 2000;
  padding: 10px 18px;
  border-radius: var(--radius-pill);
  background: var(--bg-elev);
  color: var(--text);
  font-size: 13px;
  font-weight: 500;
  box-shadow: 0 8px 24px rgba(0,0,0,0.4);
  border: 1px solid var(--hairline-strong);
  max-width: 90vw;
}

.toast-enter-active,
.toast-leave-active {
  transition: opacity 180ms var(--ease-out), transform 220ms var(--ease-spring);
}
.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translate(-50%, -8px);
}
</style>
